import { toPng } from 'html-to-image';
import * as echarts from 'echarts';

/**
 * Determines the direction of a y-axis label based on index.
 * @param {number} value - The index value.
 * @returns {string} - The direction ('left' or 'right').
 */
export function determineDirection(value) {
  if (value === 0 || value % 2 === 0) {
    return 'left';
  }
  return 'right';
}

/**
 * Size for each y-axis label.
 */
// here
const yAxioSize = 60;

/**
 * Calculates the space to render y-axis labels.
 * @param {number} input - The input value.
 * @returns {number} - The calculated value.
 */
export function calculateValue(input) {
  return Math.floor(input / 2) * yAxioSize;
}

/**
 * Finds a status date that is closest to the target date.
 * @param {Date} target - The target date.
 * @param {Array} dates - The array of dates.
 * @returns {Object} - The closest status date object.
 */
export function findClosestStatusDate(target, dates) {
  return dates.reduce((prev, curr) => (Math.abs(curr.t - target) < Math.abs(prev.t - target) ? curr : prev));
}

/**
 * Returns sensor amount for left/right based on the input side number.
 *
 * @param {number} input - Amount of sensors.
 * @returns {number[]} - Sensors in each side as an array.
 */
export const getSensorBySide = input => {
  const sensorBySide = {
    1: [1, 0],
    2: [1, 1],
    3: [2, 1],
    4: [2, 2],
    5: [3, 2],
    6: [3, 3],
    7: [4, 3],
    8: [4, 4]
  };
  return sensorBySide[input] || [0, 0];
};

/**
 * Calculates pixel value to be used as padding in the chart sides based on the amount of sensors.
 * @param {number} size - The number of sensors.
 * @returns {Array} - Array of pixel value for each chart side.
 */
export function sideSpace(size) {
  if (typeof size !== 'number' || size <= 0) {
    // Validate input is a non-negative number
    return ['30px', '30px'];
  }
  const sensorBySide = getSensorBySide(size);
  const sensorLeftSide = sensorBySide[0];
  const sensorRightSide = sensorBySide[1];
  const leftPxSize = `${sensorLeftSide * yAxioSize}px`;
  const rightPxSize = `${Math.max(sensorRightSide * yAxioSize, 30)}px`;
  return [leftPxSize, rightPxSize];
}

/**
 * Helps to get the status color.
 */
export const statusMap = {
  IDLE: '#6D5D82',
  MAINTENANCE: '#ACC8D8',
  RUNNING: '#8CB4C7',
  NODATA: '#A2A6AC',
  CRITICAL: '#C12907',
  WARNING: '#F19909',
  FUTURE: 'rgba(255, 255, 255, 0)'
};

export const autoRefreshPeriods = [
  { key: 'off', value: null },
  { key: '30s', value: 30 * 1000 },
  { key: '1m0s', value: 60 * 1000 },
  { key: '5m0s', value: 5 * 60 * 1000 }
];

/**
 * Gets a range even for negative values.
 * @param {number} begin - The beginning value.
 * @param {number} end - The ending value.
 * @returns {number} - The calculated range.
 * @throws {Error} - If both begin and end are not numbers.
 */
export function getRange(begin, end) {
  // Validate input
  if (typeof begin !== 'number' || typeof end !== 'number') {
    throw new Error('Both begin and end must be numbers');
  }
  // Calculate the range based on the conditions
  return begin < 0 ? end - begin : end;
}

/**
 * Decimates an array of data by removing elements based on a threshold.
 *
 * @param {Array} data - Array of data to be decimated.
 * @param {number} threshold - Treshold value for decimation.
 * @returns {Array} - Decimated array.
 */
export const decimateData = (data, threshold) => {
  const decimated = [];
  for (let i = 0; i < data.length; i += threshold) {
    decimated.push(data[i]);
  }
  return decimated;
};

/**
 * Clones an ECharts instance with a new width and height.
 * @param {object} chartInstance - ECharts instance to clone.
 * @param {number} width - New width for the cloned chart.
 * @param {number} height - New height for the cloned chart.
 * @returns {object} - Cloned ECharts instance.
 */
export const cloneChartInstance = (chartInstance, width, height) => {
  if (!chartInstance) return null;

  // Create an off-screen canvas to attach the cloned chart
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = width;
  tempCanvas.height = height;

  // Initialize a new ECharts instance in the off-screen canvas
  const clonedChart = echarts.init(tempCanvas, null, {
    width,
    height
  });

  // Copy all chart options from the original chart
  const chartOptions = chartInstance.getOption();
  clonedChart.setOption({ ...chartOptions, animation: false }, true);

  return clonedChart;
};

/**
 * Converts the rendered chart image to a base64 encoded string.
 * The bottom 24 pixels are cut off to remove zoom controls.
 *
 * @param {object} chartInstance - ECharts instance.
 * @param {number} maxWidth - maximum width of the resized image.
 * @param {number} maxHeight - maximum height of the resized image.
 * @returns {string} - base64 encoded image string.
 */
export const convertChartTemplate2Bas64 = (chartInstance, maxWidth = 1500, maxHeight = 580) => {
  if (!chartInstance) return '';
  // need to clone the chart instance to avoid modifying the original chart
  const clonedChart = cloneChartInstance(chartInstance, maxWidth, maxHeight);
  // render canvas from the ECharts instance
  const originalCanvas = clonedChart.getRenderedCanvas({
    type: 'png',
    pixelRatio: 1,
    backgroundColor: '#fff'
  });

  // Create an off-screen canvas for resizing
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = maxWidth;
  resizedCanvas.height = maxHeight;
  const ctx = resizedCanvas.getContext('2d');
  ctx.drawImage(originalCanvas, 0, 0, maxWidth, maxHeight + 48);

  return resizedCanvas.toDataURL('image/png', 1);
};

/**
 * Generates a data URL image from the given footer element reference.
 * It works specifically for the footer of the chart template.
 *
 * @param {React.RefObject} elementRef - reference to the element to generate the image from.
 * @param {number} width
 * @param {number} columns
 * @returns {Promise<string>} A promise that resolves to the compressed data URL of the generated image.
 */
export const generateFooterImage = (elementRef, width = 1500, columns = 4) => {
  if (!elementRef?.current) return Promise.resolve('');

  return new Promise(resolve => {
    // Clone the element
    const originalElement = elementRef.current;
    const clonedElement = originalElement.cloneNode(true);

    // Apply explicit width and grid styles
    clonedElement.style.width = `${width}px`;
    clonedElement.style.display = 'grid';

    // calculate columns size
    const colSize = Math.trunc(width / columns);
    clonedElement.style.gridTemplateColumns = `repeat(auto-fill, minmax(${colSize}px, 1fr))`;

    // Append to DOM
    document.body.appendChild(clonedElement);

    // Ensure rendering before capture
    requestAnimationFrame(() => {
      toPng(clonedElement, { backgroundColor: '#fff', quality: 1 })
        .then(dataUrl => {
          document.body.removeChild(clonedElement);
          resolve(dataUrl);
        })
        .catch(() => {
          document.body.removeChild(clonedElement);
          resolve('');
        });
    });
  });
};
